import React, { useState } from 'react';
import TruncatedText from '../admin/TruncatedText';
import {
  formatDatasetPipelineStepBody,
  formatDatasetPipelineStepLabel,
  formatDatasetThinkingTurnLabel,
  formatDatasetTurnBodyText,
  getDatasetTurnSteps,
  groupDatasetTurnsForDisplay,
  type DatasetTurnRef,
} from '../../utils/datasetTurnDisplay';
import { formatDatasetFieldValue } from '../../utils/formatDisplayNumber';

function formatDatasetSenderDisplay(senderRaw: unknown): string {
  const raw = senderRaw === null || senderRaw === undefined ? '' : String(senderRaw).trim();
  if (!raw) return '';
  const key = raw.toLowerCase();
  if (key === 'helpdesk') return 'coordinator';
  if (key === 'customer') return 'user';
  return raw;
}

function formatDatasetTurnHeader(t: Record<string, unknown>, turnIdx: number): string {
  const senderRaw = t.sender ?? t.role;
  const sender =
    senderRaw !== null && senderRaw !== undefined && String(senderRaw).trim() !== ''
      ? formatDatasetSenderDisplay(senderRaw)
      : '';
  const nameRaw = t.agent_name ?? t.agentName;
  const agentName =
    typeof nameRaw === 'string' && nameRaw.trim()
      ? nameRaw.trim()
      : nameRaw !== null && nameRaw !== undefined && (typeof nameRaw === 'number' || typeof nameRaw === 'boolean')
        ? String(nameRaw)
        : '';
  const base = sender || `turn ${turnIdx + 1}`;
  return agentName ? `${base} · ${agentName}` : base;
}

function formatDatasetFieldLabel(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

const DATASET_TURN_KNOWN_KEYS = new Set([
  'sender',
  'role',
  'agent_name',
  'agentName',
  'reaction',
  'utterances',
  'text',
  'message',
  'content',
  'steps',
  'pipeline_steps',
  'pipelineSteps',
]);

function renderExpandableDatasetText(
  value: React.ReactNode,
  opts?: { maxLength?: number; maxLines?: number }
): React.ReactNode {
  if (typeof value === 'string') {
    return (
      <TruncatedText
        text={value}
        maxLength={opts?.maxLength ?? 120}
        maxLines={opts?.maxLines ?? 3}
      />
    );
  }
  return value;
}

function DatasetThinkingBlock({
  turns,
  blockKey,
}: {
  turns: DatasetTurnRef[];
  blockKey: string;
}) {
  if (turns.length === 0) return null;
  return (
    <details className="dataset-viewer-reasoning" open={turns.length === 1}>
      <summary className="dataset-viewer-reasoning__summary">
        Thinking ({turns.length})
      </summary>
      <div className="dataset-viewer-reasoning__body">
        {turns.map(({ turn, index }, thinkingIdx) => (
          <div key={`${blockKey}-thinking-${index}`} className="dataset-viewer-reasoning__item">
            <div className="dataset-viewer-reasoning__item-label">
              {formatDatasetThinkingTurnLabel(turn, thinkingIdx)}
            </div>
            <div className="dataset-viewer-reasoning__item-body">
              <TruncatedText
                text={formatDatasetTurnBodyText(turn)}
                maxLength={240}
                maxLines={8}
              />
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

function DatasetPipelineStepsBlock({
  turn,
  blockKey,
  expanded,
  onToggle,
}: {
  turn: Record<string, unknown>;
  blockKey: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const steps = getDatasetTurnSteps(turn);
  if (steps.length === 0) return null;
  return (
    <div className="dataset-viewer-pipeline">
      <button type="button" className="dataset-viewer-pipeline__toggle" onClick={onToggle}>
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} aria-hidden />
        Reasoning ({steps.length})
      </button>
      {expanded ? (
        <div className="dataset-viewer-pipeline__steps">
          {steps.map((step, stepIdx) => (
            <div key={`${blockKey}-step-${stepIdx}`} className="dataset-viewer-pipeline__step">
              <div className="dataset-viewer-pipeline__step-label">
                {formatDatasetPipelineStepLabel(step, stepIdx)}
              </div>
              <div className="dataset-viewer-pipeline__step-body">
                <TruncatedText
                  text={formatDatasetPipelineStepBody(step)}
                  maxLength={240}
                  maxLines={8}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DatasetTurnExtras({
  turn,
  turnIdx,
  blockKey,
}: {
  turn: Record<string, unknown>;
  turnIdx: number;
  blockKey: string;
}) {
  const turnExtraFields = Object.entries(turn).filter(([key]) => !DATASET_TURN_KNOWN_KEYS.has(key));
  if (turnExtraFields.length === 0) return null;
  return (
    <div className="dataset-viewer-turn__extras">
      {turnExtraFields.map(([key, value]) => (
        <div
          className="dataset-viewer-field dataset-viewer-field--compact"
          key={`${blockKey}-turn-${turnIdx}-${key}`}
        >
          <span className="dataset-viewer-field__label">{formatDatasetFieldLabel(key)}</span>
          <div className="dataset-viewer-field__value">
            {renderExpandableDatasetText(formatDatasetFieldValue(value, key), {
              maxLength: 120,
              maxLines: 3,
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function DatasetTurnCard({
  turn,
  turnIdx,
  blockKey,
  thinkingTurns,
  variant,
}: {
  turn: Record<string, unknown>;
  turnIdx: number;
  blockKey: string;
  thinkingTurns?: DatasetTurnRef[];
  variant?: 'assistant' | 'standard' | 'reasoning';
}) {
  const [pipelineExpanded, setPipelineExpanded] = useState(false);
  const senderKey = String(turn.sender ?? turn.role ?? '').toLowerCase();
  const cardClass =
    variant === 'assistant' || senderKey === 'helpdesk' || senderKey === 'coordinator'
      ? 'dataset-viewer-turn dataset-viewer-turn--assistant'
      : variant === 'reasoning'
        ? 'dataset-viewer-turn dataset-viewer-turn--reasoning'
        : senderKey === 'user' || senderKey === 'customer'
          ? 'dataset-viewer-turn dataset-viewer-turn--user'
          : 'dataset-viewer-turn';

  return (
    <div className={cardClass}>
      <div className="dataset-viewer-turn__header">
        <span>{formatDatasetTurnHeader(turn, turnIdx)}</span>
        {typeof turn.reaction === 'string' && turn.reaction.trim() ? (
          <span className="dataset-viewer-turn__reaction">({turn.reaction.trim()})</span>
        ) : null}
      </div>

      {thinkingTurns && thinkingTurns.length > 0 ? (
        <DatasetThinkingBlock turns={thinkingTurns} blockKey={blockKey} />
      ) : null}

      <div className="dataset-viewer-turn__message">
        <TruncatedText text={formatDatasetTurnBodyText(turn)} maxLength={200} maxLines={6} />
      </div>

      <DatasetPipelineStepsBlock
        turn={turn}
        blockKey={blockKey}
        expanded={pipelineExpanded}
        onToggle={() => setPipelineExpanded((v) => !v)}
      />

      <DatasetTurnExtras turn={turn} turnIdx={turnIdx} blockKey={blockKey} />
    </div>
  );
}

export interface DatasetViewerTurnListProps {
  turns: unknown[];
  blockKeyPrefix: string;
  maxTurns?: number;
}

const DatasetViewerTurnList: React.FC<DatasetViewerTurnListProps> = ({
  turns,
  blockKeyPrefix,
  maxTurns = 20,
}) => {
  const visibleTurns = turns.slice(0, maxTurns);
  const units = groupDatasetTurnsForDisplay(visibleTurns);

  return (
    <>
      {units.map((unit, unitIdx) => {
        const blockKey = `${blockKeyPrefix}-unit-${unitIdx}`;
        if (unit.type === 'thinking_only') {
          return (
            <div key={blockKey} className="dataset-viewer-turn dataset-viewer-turn--reasoning">
              <DatasetThinkingBlock turns={unit.turns} blockKey={blockKey} />
            </div>
          );
        }
        if (unit.type === 'assistant') {
          return (
            <DatasetTurnCard
              key={blockKey}
              turn={unit.turn}
              turnIdx={unit.index}
              blockKey={blockKey}
              thinkingTurns={unit.thinkingTurns}
              variant="assistant"
            />
          );
        }
        return (
          <DatasetTurnCard
            key={blockKey}
            turn={unit.turn}
            turnIdx={unit.index}
            blockKey={blockKey}
            variant={unit.type === 'standard' ? 'standard' : 'standard'}
          />
        );
      })}
      {turns.length > maxTurns ? (
        <div className="dataset-viewer-entry__limit-note">
          Showing first {maxTurns} turns of {turns.length}.
        </div>
      ) : null}
    </>
  );
};

export default DatasetViewerTurnList;
